import { currentCompanyId } from 'src/components/property/UtilityPortalCredentials/currentCompanyId';

/**
 * AP push contract (rev 10.2). One document = one invoice = one AppFolio payable: pushing never touches
 * allocation state, the payable's amount comes from the invoice, and a succeeded document can never be
 * pushed again. Every status value below is camelCase on the wire.
 */

/** Document-level state, aggregated by the service over the document's attempt rows. */
export const APPFOLIO_PUSH_STATUS = {
  notPushed: 'notPushed',
  pending: 'pending',
  failed: 'failed',
  /** Rows name more than one AppFolio bill: a human has to reconcile, waiting never resolves it. */
  needsReconciliation: 'needsReconciliation',
  succeeded: 'succeeded'
};

/** State of one attempt row. `superseded` rows are history only. */
export const APPFOLIO_PUSH_ATTEMPT_STATUS = {
  pending: 'pending',
  blocked: 'blocked',
  failed: 'failed',
  succeeded: 'succeeded',
  superseded: 'superseded'
};

/**
 * The document states a push may be started from.
 *
 * `pending` is deliberately not one of them, though the service would take it: a re-click there is an
 * idempotent re-queue that cannot create a second payable, so the contract leaves it open. But every
 * trigger answers `pending` — that is what being queued means — so a control live on `pending` comes
 * back to life the instant it is used, reads as "that did not work", and invites the press again.
 *
 * The cost is that a push stuck `pending` — the one whose Kafka publish was lost, which has no sweep
 * yet — has no re-click to recover it from here, and needs support until that sweep exists.
 */
export const PUSHABLE_STATUSES = [APPFOLIO_PUSH_STATUS.notPushed, APPFOLIO_PUSH_STATUS.failed];

/** Both sides are guids from different services, so they may differ in case. */
export const isSameBillId = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();
const byNewestRequestedOn = (a, b) => new Date((b === null || b === void 0 ? void 0 : b.requestedOn) || 0) - new Date((a === null || a === void 0 ? void 0 : a.requestedOn) || 0);

/**
 * The row a document's state comes from: ordinarily its only attempt, otherwise the newest one that is
 * not history. Shared so every surface reads the same row — a `superseded` row picked by mistake shows
 * an outcome that has already been replaced.
 */
export const getLiveAttempt = pushStatus => {
  var _pushStatus$attempts, _ref, _attempts$find;
  const attempts = [...((_pushStatus$attempts = pushStatus === null || pushStatus === void 0 ? void 0 : pushStatus.attempts) !== null && _pushStatus$attempts !== void 0 ? _pushStatus$attempts : [])].sort(byNewestRequestedOn);
  return (_ref = (_attempts$find = attempts.find(item => item.status !== APPFOLIO_PUSH_ATTEMPT_STATUS.superseded)) !== null && _attempts$find !== void 0 ? _attempts$find : attempts[0]) !== null && _ref !== void 0 ? _ref : null;
};

/** Document-level 422 refusals, decided before anything is queued. */
export const APPFOLIO_PUSH_REFUSAL_CODES = {
  featureDisabled: 'feature_disabled',
  billNotFound: 'bill_not_found',
  documentNotValidated: 'document_not_validated',
  documentUnresolved: 'document_unresolved',
  documentIncomplete: 'document_incomplete',
  providerUnresolved: 'provider_unresolved',
  billAlreadyPushed: 'bill_already_pushed',
  /** A pending attempt holds the document at one total and this push's edited Bill total asks for another. */
  pendingAmountConflict: 'pending_attempt_amount_conflict',
  /** The invoice number sent with the push cannot identify an invoice. */
  invalidInvoiceNumber: 'invalid_invoice_number',
  /** A pending attempt is already filed under a different invoice number than this push supplies. */
  pendingInvoiceNumberConflict: 'pending_attempt_invoice_number_conflict'
};
const REFUSAL_MESSAGES = {
  [APPFOLIO_PUSH_REFUSAL_CODES.billNotFound]: 'This bill is no longer available to push.',
  [APPFOLIO_PUSH_REFUSAL_CODES.documentNotValidated]: 'The bill data has to be validated before it can be pushed.',
  [APPFOLIO_PUSH_REFUSAL_CODES.documentUnresolved]: 'No property and utility pair is resolved for this bill, so there is nothing to pay.',
  [APPFOLIO_PUSH_REFUSAL_CODES.documentIncomplete]: 'The bill has no invoice date. Complete the bill data and retry.',
  [APPFOLIO_PUSH_REFUSAL_CODES.providerUnresolved]: 'One of the bill’s property and utility pairs has no provider configured. Set it in the property utility settings and retry.',
  [APPFOLIO_PUSH_REFUSAL_CODES.billAlreadyPushed]: 'This bill has already been pushed to AppFolio.',
  [APPFOLIO_PUSH_REFUSAL_CODES.pendingAmountConflict]: 'A push for this bill is already pending at a different Bill total. Wait for it to settle, then push again with the corrected total.',
  [APPFOLIO_PUSH_REFUSAL_CODES.invalidInvoiceNumber]: 'That invoice number cannot identify an invoice. It needs at least four letters or digits, and cannot be a date or the account number.',
  [APPFOLIO_PUSH_REFUSAL_CODES.pendingInvoiceNumberConflict]: 'A push for this bill is already pending under a different invoice number. Wait for it to settle, then push again.'
};

/** Blockers decided before queueing; they arrive as an attempt with status `blocked`. */
const BLOCKER_MESSAGES = {
  invoice_number_required: 'The invoice number is required to push.',
  vendor_name_required: 'The vendor name printed on the bill is required to push.',
  gl_not_configured: 'No GL account is configured for one of the bill’s lines.',
  incomplete_amount: 'The bill’s line amounts do not cover the whole invoice.',
  partial_invoice: 'The invoice covers utilities outside the configured categories, so the payable would be partial.',
  amount_mismatch: 'The bill’s line amounts add up to more than the invoice total. Correct the bill data, or edit the Bill total.',
  amount_not_positive: 'The bill totals zero or less, so there is nothing to pay the vendor.',
  duplicate_of_other_document: 'This invoice was already pushed from another bill document.'
};

/** Failures answered by AppFolio after the push was dispatched. */
const FAILURE_MESSAGES = {
  vendor_not_mapped: 'The bill’s provider is not mapped to an AppFolio vendor.',
  multiple_vendors_mapped: 'The bill’s providers map to different AppFolio vendors, and a payable carries one.',
  property_not_mapped: 'One of the bill’s properties is not mapped in AppFolio.',
  no_appfolio_credentials: 'No AppFolio credentials are configured.',
  credentials_disabled: 'The AppFolio credentials are disabled.',
  gl_not_configured: 'No GL account is configured for one of the bill’s lines.',
  appfolio_rejected: 'AppFolio rejected the payable.',
  duplicate_reference: 'AppFolio already holds a payable for this invoice.'
};
export const getAppfolioPushRefusalMessage = code => REFUSAL_MESSAGES[code] || 'This bill could not be pushed to AppFolio.';
export const getAppfolioPushBlockerMessage = code => BLOCKER_MESSAGES[code] || 'This bill cannot be pushed yet.';

/** The blocker a missing Accounts Payable GL account raises, which the person can fix themselves. */
export const GL_NOT_CONFIGURED = 'gl_not_configured';

/**
 * Where a property's Accounts Payable GL accounts are set: its Details tab on the property dashboard, which
 * holds the default and each utility's own (LB-5981). `/edit_property` is the old design. Under the
 * property's own portfolio, not `all`: a new tab opened under `all` is sent back to All properties. Null
 * without a company, a portfolio or a property to point at.
 */
export const getPropertyDetailsPath = function (propertyLivableId, portfolioId) {
  let companyId = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : currentCompanyId();
  return companyId && portfolioId && propertyLivableId ? "/active_dashboard/".concat([companyId, portfolioId, propertyLivableId].map(encodeURIComponent).join('/'), "/details") : null;
};
export const getAppfolioPushFailureMessage = code => FAILURE_MESSAGES[code] || 'AppFolio did not create the payable for this bill.';